import { db } from '../firebase';
import {
  doc, collection, setDoc, getDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, arrayUnion, arrayRemove,
} from 'firebase/firestore';
// NOTE: orderBy is kept for comments (single-field, auto-indexed); ownerLinks sorts client-side.

function generateLinkId() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Owner operations ─────────────────────────────────────────────────────────

export async function createReviewLink({ ownerUid, fileName, ownerName, ownerDefaultCurrency, password }) {
  const linkId = generateLinkId();
  const passwordHash = password ? await sha256(password) : null;

  await Promise.all([
    setDoc(doc(db, 'reviewLinks', linkId), {
      ownerUid,
      fileName,
      ownerName: ownerName || null,
      ownerDefaultCurrency: ownerDefaultCurrency || 'USD',
      createdAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      passwordHash,
      status: 'pending',
      reviewerName: null,
      reviewerEmail: null,
    }),
    // Tag the expense file so the Firestore rule allows unauthenticated reads.
    updateDoc(doc(db, 'users', ownerUid, 'expenseFiles', fileName), {
      activeReviewLinks: arrayUnion(linkId),
    }),
  ]);
  return linkId;
}

export async function deleteReviewLink(linkId, ownerUid, fileName) {
  await Promise.all([
    deleteDoc(doc(db, 'reviewLinks', linkId)),
    updateDoc(doc(db, 'users', ownerUid, 'expenseFiles', fileName), {
      activeReviewLinks: arrayRemove(linkId),
    }),
  ]);
}

// Real-time subscription to all review links owned by uid
export function subscribeOwnerLinks(uid, callback) {
  const q = query(
    collection(db, 'reviewLinks'),
    where('ownerUid', '==', uid)
  );
  return onSnapshot(q, snap => {
    const links = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    links.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() ?? 0;
      const bt = b.createdAt?.toMillis?.() ?? 0;
      return bt - at;
    });
    callback(links);
  });
}

// ─── Public / reviewer operations ────────────────────────────────────────────

export async function getReviewLink(linkId) {
  const snap = await getDoc(doc(db, 'reviewLinks', linkId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getReviewExpenses(ownerUid, fileName) {
  const snap = await getDoc(doc(db, 'users', ownerUid, 'expenseFiles', fileName));
  if (!snap.exists()) return [];
  try { return JSON.parse(snap.data().data || '[]'); } catch { return []; }
}

export async function verifyPassword(linkId, password) {
  const link = await getReviewLink(linkId);
  if (!link) return false;
  if (!link.passwordHash) return true;
  return (await sha256(password)) === link.passwordHash;
}

// Real-time subscription to a single review link document.
// Calls callback(null) when the document is deleted so callers can react.
export function subscribeReviewLink(linkId, callback) {
  return onSnapshot(doc(db, 'reviewLinks', linkId), snap => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

export async function updateReviewStatus(linkId, { status, reviewerName, reviewerEmail }) {
  await updateDoc(doc(db, 'reviewLinks', linkId), {
    status,
    reviewerName: reviewerName || null,
    reviewerEmail: reviewerEmail || null,
    lastActivityAt: serverTimestamp(),
  });
}

// ─── Comments ─────────────────────────────────────────────────────────────────

export async function addComment(linkId, { text, authorName, authorEmail, isOwner }) {
  await addDoc(collection(db, 'reviewLinks', linkId, 'comments'), {
    text: text.trim(),
    authorName: (authorName || 'Anonymous').trim(),
    authorEmail: authorEmail ? authorEmail.trim() : null,
    isOwner: !!isOwner,
    createdAt: serverTimestamp(),
  });
  try {
    await updateDoc(doc(db, 'reviewLinks', linkId), { lastActivityAt: serverTimestamp() });
  } catch (e) {
    if (e?.code === 'not-found') throw Object.assign(new Error('LINK_DELETED'), { code: 'LINK_DELETED' });
    throw e;
  }
}

export function subscribeComments(linkId, callback) {
  const q = query(
    collection(db, 'reviewLinks', linkId, 'comments'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(q, snap =>
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
}
